import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Copy, Save, TestTube2 } from "lucide-react";
import {
  reportTemplatesApi,
  type ReportTemplateDto,
  type UpsertReportTemplatePayload,
} from "@/api/reportTemplates";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Textarea } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import {
  ReportTemplateEditBasics,
} from "./ReportTemplateEditBasics";
import {
  ReportTemplateFormEditor,
  newSection,
} from "./ReportTemplateFormEditor";

const EMPTY: UpsertReportTemplatePayload = {
  name: "",
  description: "",
  promptBody: "",
  outputFormat: "Word",
  category: "General",
  masterDocumentId: null,
  instructions: "",
  roleCodes: [],
  isActive: true,
  sections: [newSection(0)],
};

/**
 * LLM &amp; Reports v3.1 §10d — admin edit page shell. Routes:
 *   /admin/report-templates/new          → create
 *   /admin/report-templates/{id}         → edit existing
 *
 * Owns the top-level form state, save / cancel actions, and the
 * "Copy form structure from existing" + "Preview resolved prompt"
 * modals. The basics section and the embedded form editor are split
 * into ReportTemplateEditBasics + ReportTemplateFormEditor.
 */
export function ReportTemplateEditPage() {
  const { id = "new" } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  const existing = useQuery({
    // Cache key is namespaced "admin" so the editor never shares
    // cache with the user-facing /reports/generate flow (which uses
    // ["report-template", id] with a different queryFn that prepends
    // the v3.4/v3.5 virtual "Report context" section). Pre-fix the
    // two surfaces stomped on each other: whichever rendered most
    // recently determined what the cache held, so re-opening the
    // editor after touching the run page would silently load the
    // schema-injected variant — and a Save would then round-trip
    // virtual fields back to the server. Separate keys make the
    // problem impossible.
    queryKey: ["report-template", "admin", id],
    // v3.4 §1: editor uses the admin-only raw schema route so the
    // standard `_preparedFor` virtual section isn't shown as an
    // editable row.
    queryFn: () => reportTemplatesApi.adminGet(id),
    enabled: !isNew,
    // Always fetch fresh on mount so re-opening the editor after a
    // save in another tab / window picks up the latest persisted
    // values — defense against the bug report's "edit gone after
    // re-open" symptom.
    staleTime: 0,
    refetchOnMount: "always",
  });

  const [form, setForm] = useState<UpsertReportTemplatePayload>(EMPTY);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);

  useEffect(() => {
    if (!existing.data) return;
    const t = existing.data;
    setForm({
      name: t.name,
      description: t.description,
      promptBody: t.promptBody,
      outputFormat: t.outputFormat,
      category: t.category,
      masterDocumentId: t.masterDocumentId ?? null,
      instructions: t.instructions ?? "",
      roleCodes: t.roleCodes,
      isActive: t.isActive,
      sections: t.sections.map((s) => ({
        id: s.id,
        title: s.title,
        description: s.description ?? "",
        displayOrder: s.displayOrder,
        isRequired: s.isRequired,
        questions: s.questions.map((q) => ({
          id: q.id,
          questionText: q.questionText,
          helpText: q.helpText ?? "",
          questionType: q.questionType,
          optionsJson: q.optionsJson ?? "",
          validationRulesJson: q.validationRulesJson ?? "",
          isRequired: q.isRequired,
          displayOrder: q.displayOrder,
        })),
      })),
    });
  }, [existing.data]);

  const validation = useMemo(() => {
    const errs: string[] = [];
    if (!form.name.trim()) errs.push("Name is required.");
    if (!form.promptBody.trim()) errs.push("Prompt body is required.");
    if (form.sections.length === 0)
      errs.push("Form must have at least one section.");
    if (!form.sections.some((s) => s.questions.length > 0))
      errs.push("Form must have at least one question across its sections.");
    if (form.sections.some((s) => !s.title.trim()))
      errs.push("Every section must have a title.");
    if (
      form.sections.some((s) =>
        s.questions.some((q) => !q.questionText.trim()),
      )
    )
      errs.push("Every question must have text.");
    return errs;
  }, [form]);

  const saveMutation = useMutation({
    mutationFn: () =>
      isNew
        ? reportTemplatesApi.adminCreate(form)
        : reportTemplatesApi.adminUpdate(id, form),
    onSuccess: (saved: ReportTemplateDto) => {
      toast({ tone: "success", message: "Report template saved." });

      // Write the just-saved DTO directly into the editor cache so
      // the post-save view doesn't depend on a refetch round-trip
      // (which a stale browser HTTP cache or a queued background
      // request could otherwise win against). This is the cache the
      // useQuery above reads from, so the next render's existing.data
      // is the saved values immediately.
      qc.setQueryData(["report-template", "admin", saved.id], saved);

      // Invalidate the list page (so the admin grid reflects the
      // updated metadata) and the user-facing run-page cache for the
      // same template id (so the next run-time read picks up the new
      // schema rather than serving a pre-edit cached copy).
      void qc.invalidateQueries({ queryKey: ["report-templates-admin"] });
      void qc.invalidateQueries({ queryKey: ["report-template", saved.id] });
      void qc.invalidateQueries({ queryKey: ["report-template", "admin", saved.id] });

      if (isNew) navigate(`/admin/report-templates/${saved.id}`, { replace: true });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title={isNew ? "New report template" : (existing.data?.name ?? "Loading…")}
        description="One template defines a report's name, prompt body, role assignments, optional master document, and the form (sections and questions) users fill out. Saving an existing template creates a new version."
        actions={
          <div className="flex items-center gap-space-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate("/admin/report-templates")}
            >
              <ArrowLeft size={14} aria-hidden /> Back
            </Button>
            {isNew && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowCopyModal(true)}
              >
                <Copy size={14} aria-hidden /> Copy form from…
              </Button>
            )}
            {!isNew && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowTestModal(true)}
                disabled={!form.promptBody.trim()}
              >
                <TestTube2 size={14} aria-hidden /> Preview prompt
              </Button>
            )}
            <Button
              type="button"
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
              disabled={validation.length > 0}
            >
              <Save size={14} aria-hidden /> {isNew ? "Create" : "Save"}
            </Button>
          </div>
        }
      />

      {validation.length > 0 && (
        <div className="bg-status-warning/10 border border-status-warning rounded-md p-space-3 mb-space-3">
          <div className="font-display font-bold text-small text-status-warning mb-space-1">
            Cannot save yet
          </div>
          <ul className="list-disc pl-space-4 text-caption text-status-warning">
            {validation.map((v) => (
              <li key={v}>{v}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-space-4 max-w-reading mx-auto">
        <ReportTemplateEditBasics
          form={form}
          setForm={setForm}
          templateId={isNew ? null : id}
        />
        <ReportTemplateFormEditor form={form} setForm={setForm} />
      </div>

      {showCopyModal && (
        <CopyFormFromExistingModal
          targetId={isNew ? null : id}
          form={form}
          setForm={setForm}
          onClose={() => setShowCopyModal(false)}
        />
      )}

      {showTestModal && !isNew && (
        <TestPreviewModal
          templateId={id}
          onClose={() => setShowTestModal(false)}
        />
      )}
    </>
  );
}

/**
 * Spec §5b — "Copy form structure from existing template". For a brand-
 * new template (no ID yet), we deep-copy the source's sections client-
 * side into the in-memory form so the user can save once. For an
 * existing template the backend's POST /copy-form-from/{sourceId}
 * handles the deep copy and returns the updated template.
 */
function CopyFormFromExistingModal({
  targetId,
  form,
  setForm,
  onClose,
}: {
  targetId: string | null;
  form: UpsertReportTemplatePayload;
  setForm: React.Dispatch<React.SetStateAction<UpsertReportTemplatePayload>>;
  onClose: () => void;
}) {
  const list = useQuery({
    queryKey: ["report-templates-admin", "copy-from-list"],
    queryFn: () => reportTemplatesApi.adminList({ page: 1, pageSize: 100 }),
  });
  const [picked, setPicked] = useState<string>("");
  const { toast } = useToast();
  const qc = useQueryClient();

  const apply = useMutation({
    mutationFn: async () => {
      // v3.4 §1: copy from the raw admin schema so the source's
      // virtual `_preparedFor` section doesn't get duplicated into
      // the target's persisted definition.
      const source = await reportTemplatesApi.adminGet(picked);
      if (targetId) {
        // Persist the copy on the server, which deep-copies sections +
        // questions with new IDs and a freshly-allocated FK to the target.
        return reportTemplatesApi.adminCopyFormFrom(targetId, picked).then(
          (r) => ({ kind: "remote" as const, template: r }),
        );
      }
      return { kind: "local" as const, template: source };
    },
    onSuccess: ({ kind, template }) => {
      // Apply the copied structure into local form state regardless of
      // which path ran, so the user sees it immediately.
      setForm({
        ...form,
        sections: template.sections.map((s) => ({
          // Drop source IDs so the server treats them as new on save (for
          // the local-copy / new-template path). The server-deep-copy
          // path already returns fresh IDs.
          id: kind === "remote" ? s.id : null,
          title: s.title,
          description: s.description ?? "",
          displayOrder: s.displayOrder,
          isRequired: s.isRequired,
          questions: s.questions.map((q) => ({
            id: kind === "remote" ? q.id : null,
            questionText: q.questionText,
            helpText: q.helpText ?? "",
            questionType: q.questionType,
            optionsJson: q.optionsJson ?? "",
            validationRulesJson: q.validationRulesJson ?? "",
            isRequired: q.isRequired,
            displayOrder: q.displayOrder,
          })),
        })),
      });
      toast({
        tone: "success",
        message: `Copied form from '${template.name}'. Your edits won't affect the source template.`,
      });
      if (targetId)
      {
        // Invalidate both the user-facing run cache key and the
        // admin editor key so the post-copy schema is fresh on
        // either surface, plus the admin list (incl. its
        // ["report-templates-admin","copy-from-list"] variant — the
        // grid shows section/question counts the copy just changed).
        void qc.invalidateQueries({ queryKey: ["report-template", targetId] });
        void qc.invalidateQueries({ queryKey: ["report-template", "admin", targetId] });
        void qc.invalidateQueries({ queryKey: ["report-templates-admin"] });
      }
      onClose();
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Copy form structure from existing template"
      description="Start this template's sections and questions from a copy of another template. The copy is a one-time starting point — changes you make from here on don't affect the source."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => apply.mutate()}
            loading={apply.isPending}
            disabled={!picked}
          >
            Copy
          </Button>
        </>
      }
    >
      {list.isLoading ? (
        <p className="text-caption text-gray-mid">Loading templates…</p>
      ) : (list.data?.data.length ?? 0) === 0 ? (
        <p className="text-caption text-gray-mid italic">
          No templates available to copy from.
        </p>
      ) : (
        <ul className="flex flex-col gap-space-1 max-h-[24rem] overflow-y-auto">
          {list.data?.data.map((t) => (
            <li key={t.id}>
              <label
                className={`block p-space-2 rounded-md border cursor-pointer ${
                  picked === t.id
                    ? "border-gold bg-gold/10"
                    : "border-border-light hover:bg-white-warm"
                }`}
              >
                <input
                  type="radio"
                  className="sr-only"
                  name="copy-source"
                  checked={picked === t.id}
                  onChange={() => setPicked(t.id)}
                />
                <div className="font-semibold text-onyx">{t.name}</div>
                <div className="text-caption text-gray-mid">
                  {t.category} · {t.sections.length} sections ·{" "}
                  {t.sections.reduce((acc, s) => acc + s.questions.length, 0)}{" "}
                  questions
                </div>
              </label>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/**
 * Spec §5a / §9e — admin "Test" preview. Renders the resolved prompt
 * against an empty / synthetic submission so the admin can verify the
 * placeholder substitution looks right. PR 3 keeps the synthetic-input
 * editor lightweight (just one-time edit pass-through) — a richer form
 * mocker can land in a future iteration.
 */
function TestPreviewModal({
  templateId,
  onClose,
}: {
  templateId: string;
  onClose: () => void;
}) {
  const [edit, setEdit] = useState("");

  const test = useMutation({
    mutationFn: () =>
      reportTemplatesApi.adminTest(templateId, {
        reportSubmissionId: null,
        supportingDocumentIds: null,
        oneTimePromptEdit: edit || null,
      }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Preview resolved prompt"
      description="Shows what the prompt looks like once the report fields have been filled in. Useful for sanity-checking the prompt body before saving."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => test.mutate()} loading={test.isPending}>
            Render
          </Button>
        </>
      }
    >
      <div className="space-y-space-3">
        <Textarea
          rows={4}
          placeholder="Optional one-time edit to the prompt body before rendering…"
          value={edit}
          onChange={(e) => setEdit(e.target.value)}
        />
        {test.isError && (
          <p className="text-caption text-status-error" role="alert">
            {(test.error as Error).message}
          </p>
        )}
        {test.data && (
          <Textarea
            rows={20}
            readOnly
            className="font-mono text-caption"
            value={test.data.resolvedPrompt}
          />
        )}
      </div>
    </Modal>
  );
}
